import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Autocomplete, TextField, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, InputLabel, Select, MenuItem, Checkbox } from '@mui/material';
import { CheckCircle, Error, Schedule } from '@mui/icons-material';
import { toast } from 'sonner';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { usePermissions } from '../api/auth/PermissionsContext';
import { fetchClients } from '../api/features/clientData/clientApi';
import { fetchJobNames, fetchJobDetails, fetchJobDetailsCount, createJobDetail, JobDetailCreatePayload } from '../api/features/jobDetail/jobDetailApi';
import { epochToDateTime } from '../api/utils';
import { PageLayout, FilterBar, DrillDownTable, SAPIconButton } from '../components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTableUrlState } from '../hooks/useTableUrlState';
import { logger } from '../utils/logger';
import { api } from '../api/fetchWrapper';
import { API_ENDPOINTS, getEndpoint } from '../api/apiConstants';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const JobDetails = () => {
  const { clientCode } = useClientCode();
  const { permissions } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = clientCode === 'admin';
  // JOB_DETAILS = job details screen view; JOB_DETAIL_EDIT = job detail tab view, edit (required to open edit page)
  const canNavigateToJobDetailEdit = isAdmin && permissions.includes('JOB_DETAIL_EDIT');

  // Add Job icon only for users who have all: JOB_DETAILS, JOB_DETAIL_EDIT, ALLOW_JOB_SETTINGS_EDIT
  const canShowAddJob =
    permissions.includes('JOB_DETAILS') &&
    permissions.includes('JOB_DETAIL_EDIT') &&
    permissions.includes('ALLOW_JOB_SETTINGS_EDIT');

  const { page, rowsPerPage, setPage, setRowsPerPage, syncToUrl, getParam, searchParams } = useTableUrlState({
    pathname: '/job-details',
    defaultPage: 0,
    defaultRowsPerPage: 10,
  });

  const [client, setClient] = useState<{ label: string; value: number; code?: string } | null>(null);
  const [job, setJob] = useState<{ label: string; value: string } | null>(null); // For client users (autocomplete)
  const [jobNameText, setJobNameText] = useState<string>(''); // For admin users (text input)
  const [clientOptions, setClientOptions] = useState<{ label: string; value: number; code?: string }[]>([]);
  const [jobOptions, setJobOptions] = useState<{ label: string; value: string }[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowCount, setRowCount] = useState(0);
  const [initialClientId, setInitialClientId] = useState<number | null>(null);
  const [initialJobName, setInitialJobName] = useState<string | null>(null);

  // Enums for create modal (status, interval types)
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [scheduleTypeOptions, setScheduleTypeOptions] = useState<string[]>([]);
  const [intervalTypeOptions, setIntervalTypeOptions] = useState<string[]>([]);

  // Create Job modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmCreateOpen, setConfirmCreateOpen] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createForm, setCreateForm] = useState<{
    jobName: string;
    jobCode: string;
    timeIntervalType: string;
    intervalType: string;
    status: string;
    nextRunTime: string;
    lastScheduledStartTime: string;
    interval: number | '';
    retryEnabled: boolean;
    isAutoRetryEnabled: boolean;
    agentJob: boolean;
  }>({
    jobName: '',
    jobCode: '',
    timeIntervalType: '',
    intervalType: '',
    status: '',
    nextRunTime: '',
    lastScheduledStartTime: '',
    interval: '',
    retryEnabled: true,
    isAutoRetryEnabled: false,
    agentJob: false,
  });

  // Load client options (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    fetchClients({ clientCode, firstResult: -1, maxResult: 0 })
      .then((clients) => {
        setClientOptions((clients || []).map((c: any) => ({ label: c.name, value: c.id, code: c.code })));
      })
      .catch(() => setClientOptions([]));
  }, [clientCode, isAdmin]);

  // Load job detail enums for status / interval types (used in create modal)
  useEffect(() => {
    if (!isAdmin) return;
    const fetchEnums = async () => {
      try {
        const enumsEndpoint = getEndpoint(clientCode, API_ENDPOINTS.JOB_DETAIL_EDIT.GET_ENUMS);
        const enumsResponse = await api.get<any>(enumsEndpoint);
        if (Array.isArray(enumsResponse)) {
          setStatusOptions(Array.isArray(enumsResponse[0]) ? enumsResponse[0] : []);
          setScheduleTypeOptions(Array.isArray(enumsResponse[1]) ? enumsResponse[1] : []);
          setIntervalTypeOptions(Array.isArray(enumsResponse[2]) ? enumsResponse[2] : []);
        }
      } catch (e) {
        logger.error('Failed to load job enums', e);
      }
    };
    fetchEnums();
  }, [clientCode, isAdmin]);

  // Restore initial clientId/jobName from URL
  useEffect(() => {
    const cid = getParam('clientId');
    const jn = getParam('jobName');
    setInitialClientId(cid ? Number(cid) : null);
    setInitialJobName(jn || null);
  }, [getParam, searchParams.clientId, searchParams.jobName]);

  // Restore client selection when options load
  useEffect(() => {
    if (client != null || !initialClientId || clientOptions.length === 0) return;
    const match = clientOptions.find((o) => Number(o.value) === Number(initialClientId));
    if (match) setClient(match);
  }, [clientOptions, initialClientId, client]);

  // Restore job selection when options load (for client users)
  useEffect(() => {
    if (!isAdmin) {
      if (job != null || !initialJobName || jobOptions.length === 0) return;
      const match = jobOptions.find((o) => String(o.value) === String(initialJobName));
      if (match) setJob(match);
    }
  }, [jobOptions, initialJobName, job, isAdmin]);

  // Restore job name text for admin users (immediate, doesn't depend on options)
  useEffect(() => {
    if (isAdmin && initialJobName && !jobNameText) {
      setJobNameText(initialJobName);
    }
  }, [initialJobName, isAdmin]); // Removed jobNameText from deps to avoid loops

  // Load job options
  // - Admin: when a client is selected (optional, for autocomplete suggestions)
  // - Client users: once on mount, using their implicit clientCode
  useEffect(() => {
    if (isAdmin) {
      // For admin users, job options are optional (only load if client is selected for autocomplete)
      // But we don't require it - user can type freely in text field
      if (client?.value) {
        fetchJobNames({ clientCode, clientId: client.value, firstResult: 0, maxResult: 0, searched: true })
          .then((names) => setJobOptions((names || []).map((n) => ({ label: n, value: n }))))
          .catch(() => setJobOptions([]));
      } else {
        setJobOptions([]);
      }
      return;
    }

    // For client users, fetch jobs based on their clientCode (no explicit client selector)
    fetchJobNames({ clientCode, firstResult: 0, maxResult: 0, searched: true })
      .then((names) => setJobOptions((names || []).map((n) => ({ label: n, value: n }))))
      .catch(() => setJobOptions([]));
  }, [client, clientCode, isAdmin]);

  // Refetch when URL list state changes (page, rpp, or filters)
  const clientIdParam = getParam('clientId');
  const jobNameParam = getParam('jobName');
  // Allow search with no filters (just searched=true) - check if searched param exists or if we have filters
  const hasSearched = searchParams.searched === 'true' || !!(clientIdParam?.trim() || jobNameParam?.trim());
  const hasFilters = hasSearched;
  useEffect(() => {
    if (!hasFilters) return;
    setLoading(true);
    setError(null);
    const firstResult = page * rowsPerPage;
    const searchParamsApi = {
      clientCode,
      firstResult,
      maxResult: rowsPerPage,
      clientId: clientIdParam ? Number(clientIdParam) : undefined,
      jobName: jobNameParam?.trim() || undefined,
    };
    const countParams = {
      clientCode,
      clientId: clientIdParam ? Number(clientIdParam) : undefined,
      jobName: jobNameParam?.trim() || undefined,
    };
    Promise.all([fetchJobDetails(searchParamsApi), fetchJobDetailsCount(countParams)])
      .then(([response, totalCount]) => {
        setRows(response || []);
        setRowCount(totalCount);
      })
      .catch((err) => {
        setError(err?.message || 'Failed to fetch job details');
        setRows([]);
        setRowCount(0);
      })
      .finally(() => setLoading(false));
  }, [page, rowsPerPage, clientIdParam, jobNameParam, clientCode, searchParams.searched]);

  const handleSearch = useCallback(() => {
    // For admin: use jobNameText (text input), for client: use job (autocomplete)
    const clientId = client?.value ?? null;
    const jobName = isAdmin ? jobNameText.trim() : (job?.value ?? null);
    
    // Determine if we have any filters
    const hasClientFilter = clientId != null;
    const hasJobNameFilter = jobName && jobName.length > 0;
    
    // Build URL params - always include searched=true
    const urlParams: Record<string, string> = {
      page: '0',
      rpp: String(rowsPerPage),
      searched: 'true',
    };
    
    if (hasClientFilter) {
      urlParams.clientId = String(clientId);
    }
    if (hasJobNameFilter) {
      urlParams.jobName = jobName;
    }
    
    syncToUrl(urlParams);
    setLoading(true);
    setError(null);
    
    const searchParamsApi = {
      clientCode,
      firstResult: 0,
      maxResult: rowsPerPage,
      clientId: hasClientFilter ? clientId : undefined,
      jobName: hasJobNameFilter ? jobName : undefined,
    };
    const countParams = {
      clientCode,
      clientId: hasClientFilter ? clientId : undefined,
      jobName: hasJobNameFilter ? jobName : undefined,
    };
    
    Promise.all([fetchJobDetails(searchParamsApi), fetchJobDetailsCount(countParams)])
      .then(([response, totalCount]) => {
        setRows(response || []);
        setRowCount(totalCount);
      })
      .catch((err) => {
        setError(err?.message || 'Failed to fetch job details');
        setRows([]);
        setRowCount(0);
      })
      .finally(() => setLoading(false));
  }, [client, job, jobNameText, isAdmin, rowsPerPage, clientCode, syncToUrl]);

  const handleReset = useCallback(() => {
    setClient(null);
    setJob(null);
    setJobNameText('');
    setRows([]);
    setRowCount(0);
    setError(null);
    setInitialClientId(null);
    setInitialJobName(null);
    navigate({ pathname: '/job-details' }, { replace: true });
  }, [navigate]);

  const handleRowClick = useCallback(
    (row: any) => {
      if (!canNavigateToJobDetailEdit) return;
      const listSearch = location.pathname + location.search;
      navigate(`/job-detail-edit/${row.id}`, {
        state: { fromListSearch: listSearch },
      });
    },
    [canNavigateToJobDetailEdit, location.pathname, location.search, navigate]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
    },
    [setPage]
  );

  const handleRowsPerPageChange = useCallback(
    (rpp: number) => {
      setRowsPerPage(rpp);
    },
    [setRowsPerPage]
  );

  const processedRows = useMemo(() => {
    return rows.map((row, idx) => {
      let statusLabel = 'Unknown';
      let statusType: 'success' | 'warning' | 'error' | 'info' = 'info';
      
      if (row[6] === 1) {
        const dt = new Date(row[5]);
        try {
          dt.setMinutes(dt.getMinutes() + (Number(row[1]) || 0));
        } catch (_) {}
        if (dt < new Date()) {
          statusLabel = 'Stopped';
          statusType = 'error';
        } else {
          statusLabel = 'Running';
          statusType = 'info'; // Use info/blue instead of warning/orange for better distinction
        }
      } else if (row[6] === 0) {
        statusLabel = 'Will Run Again';
        statusType = 'success';
      }
      
      return {
        id: row[0] ?? idx,
        serialNumber: page * rowsPerPage + idx + 1,
        jobName: row[2] || 'N/A',
        lastStart: epochToDateTime(row[3]) || 'N/A',
        lastEnd: epochToDateTime(row[4]) || 'N/A',
        nextRun: epochToDateTime(row[5]) || 'N/A',
        interval: row[1] || 'N/A',
        statusLabel,
        statusType,
      };
    });
  }, [rows, page, rowsPerPage]);

  const columns = useMemo(
    () => [
      { 
        id: 'serialNumber', 
        label: 'S.No.', 
        minWidth: '70px' as const,
        align: 'center' as const,
      },
      { 
        id: 'jobName', 
        label: 'Job Name',
      },
      { 
        id: 'status', 
        label: 'Status',
        align: 'center' as const,
        minWidth: '140px' as const,
        render: (row: any) => {
          const getStatusConfig = (statusType: string) => {
            switch (statusType) {
              case 'success':
                return {
                  color: 'success' as const,
                  icon: <CheckCircle sx={{ fontSize: 16 }} />,
                };
              case 'info':
                return {
                  color: 'info' as const,
                  icon: <Schedule sx={{ fontSize: 16 }} />,
                };
              case 'error':
                return {
                  color: 'error' as const,
                  icon: <Error sx={{ fontSize: 16 }} />,
                };
              default:
                return {
                  color: 'default' as const,
                  icon: null,
                };
            }
          };
          
          const config = getStatusConfig(row.statusType);
          
          return (
            <Chip
              icon={config.icon ?? undefined}
              label={row.statusLabel}
              size="small"
              color={config.color}
              variant="outlined"
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                height: '28px',
                borderWidth: '1.5px',
                '& .MuiChip-icon': {
                  color: 'inherit',
                },
              }}
            />
          );
        },
      },
      { 
        id: 'lastStart', 
        label: 'Last Start Time',
        align: 'right' as const,
      },
      { 
        id: 'lastEnd', 
        label: 'Last End Time',
        align: 'right' as const,
      },
      { 
        id: 'nextRun', 
        label: 'Next Run Time',
        align: 'right' as const,
      },
      { 
        id: 'interval', 
        label: 'Interval',
        align: 'right' as const,
      },
    ],
    []
  );

  const openCreateJobModal = useCallback(() => {
    if (!client) return;
    const nowIso = new Date().toISOString().slice(0, 16); // for datetime-local
    setCreateForm({
      jobName: '',
      jobCode: '',
      timeIntervalType: intervalTypeOptions[0] || 'MONTHLY',
      intervalType: scheduleTypeOptions[0] || 'INTERVAL_AFTER_LAST_FINISH',
      status: statusOptions[0] || 'RUNNING',
      nextRunTime: nowIso,
      lastScheduledStartTime: '',
      interval: '',
      retryEnabled: true,
      isAutoRetryEnabled: false,
      agentJob: false,
    });
    setCreateOpen(true);
  }, [client, intervalTypeOptions, scheduleTypeOptions, statusOptions]);

  const handleCreateJobRequestSave = useCallback(() => {
    if (!client) {
      toast.error('Please select a client before creating a job');
      return;
    }
    const name = createForm.jobName.trim();
    const code = createForm.jobCode.trim();

    if (!name || !code) {
      toast.error('Job Name and Job Code are required');
      return;
    }

    // Job code: only letters and underscores, underscore allowed only between letters
    const codeRegex = /^[A-Za-z]+(?:_[A-Za-z]+)*$/;
    if (!codeRegex.test(code)) {
      toast.error('Job Code can only contain letters and underscores, and must start with a letter');
      return;
    }

    if (!createForm.timeIntervalType || !createForm.intervalType || !createForm.status) {
      toast.error('Please select Status, Time Interval Type, and Interval Type');
      return;
    }

    const normalizeDate = (value: string) => {
      if (!value) return null;
      const trimmed = value.trim().replace(' ', 'T');
      const d = new Date(trimmed);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    const nextRunDate = normalizeDate(createForm.nextRunTime) || new Date();
    if (Number.isNaN(nextRunDate.getTime())) {
      toast.error('Please provide a valid Next Run Time');
      return;
    }

    if (createForm.lastScheduledStartTime) {
      const d = normalizeDate(createForm.lastScheduledStartTime);
      if (!d) {
        toast.error('Please provide a valid Last Scheduled Start Time');
        return;
      }
    }

    setConfirmCreateOpen(true);
  }, [client, createForm]);

  const handleCreateJobSave = useCallback(async () => {
    if (!client) {
      toast.error('Please select a client before creating a job');
      return;
    }
    const normalizeDate = (value: string) => {
      if (!value) return null;
      const trimmed = value.trim().replace(' ', 'T');
      const d = new Date(trimmed);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    const nextRunDate = normalizeDate(createForm.nextRunTime) || new Date();

    const payload: JobDetailCreatePayload = {
      jobName: createForm.jobName.trim(),
      jobCode: createForm.jobCode.trim(),
      clientId: client.value,
      timeIntervalType: createForm.timeIntervalType,
      intervalType: createForm.intervalType,
      status: createForm.status,
      nextRunTime: nextRunDate.toISOString(),
      retryEnabled: createForm.retryEnabled,
      isAutoRetryEnabled: createForm.isAutoRetryEnabled,
    };

    const extra: any = {};
    if (createForm.lastScheduledStartTime) {
      const d = normalizeDate(createForm.lastScheduledStartTime);
      if (d) {
        extra.lastScheduledStartTime = d.toISOString();
      }
    }
    extra.agentJob = createForm.agentJob;
    if (createForm.interval !== '' && Number.isFinite(Number(createForm.interval))) {
      extra.interval = Number(createForm.interval);
    }

    try {
      setCreateSaving(true);
      await createJobDetail({ clientCode, payload: { ...payload, ...extra } });
      toast.success('Job created successfully');
      setCreateOpen(false);
      setConfirmCreateOpen(false);
      if (isAdmin) {
        setJobNameText(createForm.jobName.trim());
      }
      // Refresh list with current filters
      handleSearch();
    } catch (e: any) {
      logger.error('Failed to create job detail', e);
      toast.error(e?.message || 'Failed to create job');
    } finally {
      setCreateSaving(false);
    }
  }, [client, clientCode, createForm, handleSearch, isAdmin]);

  const canCreateJob = isAdmin && !!client;
  const isIntervalRequired = createForm.timeIntervalType === 'RELATIVE' || createForm.timeIntervalType === 'MONTHLY';
  const hasValidInterval = !isIntervalRequired || (createForm.interval !== '' && Number(createForm.interval) >= 0);

  const jobCodeRegex = /^[A-Za-z]+(?:_[A-Za-z]+)*$/;
  const jobCodeFilled = createForm.jobCode.trim().length > 0;
  const jobCodeValid = !jobCodeFilled || jobCodeRegex.test(createForm.jobCode.trim());
  const jobCodeHelper =
    !jobCodeFilled
      ? 'Job Code: letters and underscores, must start with a letter'
      : jobCodeValid
      ? 'Job Code: letters and underscores, must start with a letter'
      : 'Job Code can only contain letters and underscores, and must start with a letter';

  const isCreateFormValid =
    !!client &&
    !!createForm.jobName.trim() &&
    !!createForm.jobCode.trim() &&
    jobCodeValid &&
    !!createForm.timeIntervalType &&
    !!createForm.intervalType &&
    !!createForm.nextRunTime &&
    hasValidInterval;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageLayout
        title="Job Details"
        description="Search and manage job details for your clients"
      >
      <FilterBar loading={loading}>
        {isAdmin && (
          <Box
            sx={{
              flex: { xs: '1 1 100%', md: '1 1 0' },
              minWidth: { xs: '100%', sm: 200 },
              maxWidth: { md: 'none' },
            }}
          >
            <Autocomplete
              options={clientOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => String(a.value) === String(b.value)}
              value={client}
              onChange={(_, v) => {
                setClient(v);
                if (v == null) {
                  handleReset();
                }
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Client" fullWidth />}
            />
          </Box>
        )}
        {isAdmin ? (
          // For admin users: TextField (free text input, always enabled)
          <Box
            sx={{
              flex: { xs: '1 1 100%', md: '1 1 0' },
              minWidth: { xs: '100%', sm: 200 },
              maxWidth: { md: 'none' },
            }}
          >
            <TextField
              label="Job Name"
              value={jobNameText}
              onChange={(e) => setJobNameText(e.target.value)}
              placeholder="Enter job name (partial match)"
              fullWidth
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
          </Box>
        ) : (
          // For client users: Autocomplete (filtered by their clientCode)
          <Box
            sx={{
              flex: { xs: '1 1 100%', md: '1 1 0' },
              minWidth: { xs: '100%', sm: 200 },
              maxWidth: { md: 'none' },
            }}
          >
            <Autocomplete
              options={jobOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => String(a.value) === String(b.value)}
              value={job}
              onChange={(_, v) => setJob(v)}
              renderOption={(props, option) => (
                <li {...props} key={option.value ?? option.label}>
                  {option.label}
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Job Name" fullWidth />}
            />
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            flexShrink: 0,
            alignSelf: { xs: 'stretch', md: 'center' },
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            width: { xs: '100%', md: 'auto' },
            ml: { md: 2 },
          }}
        >
          <SAPIconButton icon="search" variant="primary" onClick={handleSearch} disabled={loading} aria-label="Search" />
          <SAPIconButton icon="reset" variant="secondary" onClick={handleReset} disabled={loading} aria-label="Reset" />
          {isAdmin && canShowAddJob && (
            <SAPIconButton
              icon="add"
              variant="success"
              onClick={openCreateJobModal}
              disabled={!canCreateJob}
              tooltip={canCreateJob ? 'Create new job for selected client' : 'Select a client first'}
              aria-label="Create job"
            />
          )}
        </Box>
      </FilterBar>

      {(processedRows.length > 0 || loading || error) ? (
        <DrillDownTable
          columns={columns}
          rows={processedRows}
          getRowId={(row) => row.id}
          loading={loading}
          error={error}
          pagination={{
            page,
            rowsPerPage,
            rowCount,
            onPageChange: handlePageChange,
            onRowsPerPageChange: handleRowsPerPageChange,
            rowsPerPageOptions: [5, 10, 25, 50],
          }}
          onRowClick={canNavigateToJobDetailEdit ? handleRowClick : undefined}
          emptyMessage="No job details found. Try searching with different criteria."
        />
      ) : (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="body1" color="text.secondary">
            {isAdmin 
              ? 'Enter filters (optional) and click Search to view job details. You can search with no filters, client only, job name only, or both.'
              : 'Select filters and click Search to view job details.'}
          </Typography>
        </Box>
      )}

      {/* Create Job Detail Modal */}
      <Dialog
        open={createOpen}
        onClose={() => !createSaving && setCreateOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Job Detail</DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              columnGap: 3,
              rowGap: 2.5,
              mt: 1,
            }}
          >
            <TextField
              label="Job Name"
              value={createForm.jobName}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, jobName: e.target.value }))}
              fullWidth
              size="small"
              required
            />
            <TextField
              label="Job Code"
              value={createForm.jobCode}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, jobCode: e.target.value }))}
              fullWidth
              size="small"
              required
              error={!jobCodeValid && jobCodeFilled}
              helperText={jobCodeHelper}
            />

            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={createForm.status}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, status: String(e.target.value) }))
                }
              >
                {statusOptions.map((opt, idx) => (
                  <MenuItem key={opt ? `${opt}-${idx}` : idx} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Time Interval Type</InputLabel>
              <Select
                label="Time Interval Type"
                value={createForm.timeIntervalType}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, timeIntervalType: String(e.target.value) }))
                }
              >
                {intervalTypeOptions.map((opt, idx) => (
                  <MenuItem key={opt ? `${opt}-${idx}` : idx} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Interval Type</InputLabel>
              <Select
                label="Interval Type"
                value={createForm.intervalType}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, intervalType: String(e.target.value) }))
                }
              >
                {scheduleTypeOptions.map((opt, idx) => (
                  <MenuItem key={opt ? `${opt}-${idx}` : idx} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(createForm.timeIntervalType === 'RELATIVE' || createForm.timeIntervalType === 'MONTHLY') && (
              <TextField
                label="Interval"
                type="number"
                size="small"
                fullWidth
                value={createForm.interval === '' ? '' : createForm.interval}
                onChange={(e) => {
                  const v = e.target.value;
                  setCreateForm((prev) => ({
                    ...prev,
                    interval: v === '' ? '' : Math.max(0, Number(v)),
                  }));
                }}
                inputProps={{ min: 0 }}
                helperText={
                  createForm.timeIntervalType === 'MONTHLY'
                    ? 'Interval in minutes (e.g. 43200 for 30 days)'
                    : 'Interval in minutes'
                }
              />
            )}

            <DateTimePicker
              label="Next Run Time"
              value={createForm.nextRunTime ? new Date(createForm.nextRunTime) : null}
              onChange={(newValue) =>
                setCreateForm((prev) => ({
                  ...prev,
                  nextRunTime: newValue ? new Date(newValue).toISOString() : '',
                }))
              }
              ampm={false}
              views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
              format="yyyy-MM-dd HH:mm:ss"
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  helperText: 'Pick or type date and time (yyyy-mm-dd hh:mm:ss)',
                },
              }}
            />
            <DateTimePicker
              label="Last Scheduled Start Time"
              value={createForm.lastScheduledStartTime ? new Date(createForm.lastScheduledStartTime) : null}
              onChange={(newValue) =>
                setCreateForm((prev) => ({
                  ...prev,
                  lastScheduledStartTime: newValue ? new Date(newValue).toISOString() : '',
                }))
              }
              ampm={false}
              views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
              format="yyyy-MM-dd HH:mm:ss"
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  helperText: 'Optional, yyyy-mm-dd hh:mm:ss format',
                },
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                checked={createForm.retryEnabled}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, retryEnabled: e.target.checked }))
                }
              />
              <Typography variant="body2">Retry Enabled</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                checked={createForm.agentJob}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, agentJob: e.target.checked }))
                }
              />
              <Typography variant="body2">Agent Job</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                checked={createForm.isAutoRetryEnabled}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, isAutoRetryEnabled: e.target.checked }))
                }
              />
              <Typography variant="body2">Automatic Retry</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateOpen(false)}
            disabled={createSaving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateJobRequestSave}
            disabled={createSaving || !isCreateFormValid}
          >
            {createSaving ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog for job creation */}
      <Dialog
        open={confirmCreateOpen}
        onClose={() => !createSaving && setConfirmCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Job Creation</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            Do you want to create job <strong>{createForm.jobName || '(no name)'}</strong> for
            client <strong>{client?.label}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmCreateOpen(false)}
            disabled={createSaving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateJobSave}
            disabled={createSaving}
          >
            {createSaving ? 'Saving...' : 'Yes, Create'}
          </Button>
        </DialogActions>
      </Dialog>
      </PageLayout>
    </LocalizationProvider>
  );
};

export default JobDetails;
